"use client";

import React, { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { errorLogger } from "@/lib/utils/error-logger";
import { getTranslation } from "@/lib/i18n/translations";
import { defaultLocale } from "@/i18n/config";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // In development, log component stack so "fewer hooks" errors can be traced to the offending component
    if (process.env.NODE_ENV === "development" && errorInfo.componentStack) {
      console.error("[ErrorBoundary] Component stack (identifies which component threw):", errorInfo.componentStack);
    }
    // Log error using error logger
    errorLogger.error(
      "ErrorBoundary caught an error",
      {
        componentStack: errorInfo.componentStack,
        errorName: error.name,
      },
      error,
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Get locale from document or default to English
      const locale = typeof document !== "undefined" 
        ? (document.documentElement.lang || defaultLocale) as typeof defaultLocale
        : defaultLocale;

      const t = (key: string) => getTranslation(locale, key);

      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card padding="lg" className="max-w-md w-full" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="space-y-4" style={{ width: '100%' }}>
              <div className="text-center" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                <div className="text-5xl">⚠️</div>
                <h1 className="mt-2 text-2xl font-semibold text-neutral-900" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                  {t("errors.somethingWentWrong")}
                </h1>
              </div>
              <p className="text-sm text-neutral-600" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%', display: 'block' }}>
                {t("errors.serverError")}
              </p>
              {this.state.error && process.env.NODE_ENV === "development" && (
                <details className="mt-4" style={{ width: '100%' }}>
                  <summary className="cursor-pointer text-sm font-medium text-neutral-600 hover:text-neutral-900" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    Error details (development only)
                  </summary>
                  <pre className="mt-2 overflow-auto rounded-xl bg-neutral-100 p-3 text-xs text-neutral-700 whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', width: '100%' }}>
                    {this.state.error.toString()}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button onClick={this.handleReset} variant="secondary" className="w-full sm:w-auto">
                  {t("errors.tryAgain")}
                </Button>
                <Button
                  onClick={() => {
                    window.location.href = "/dashboard";
                  }}
                  className="w-full sm:w-auto"
                >
                  {t("common.dashboard")}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
